/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.22.0
 * @since 1.0.0
 */
import { ChangeEvent, useState } from 'react';

import get from 'lodash/get';

import LinkBlock from '@components/common/LinkBlock/LinkBlock';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import { ICreateInquiryTranslation } from '@modules/internationalization/types/corporate';

import { uploadFilesValidation } from '../../createInquiryValidation';
import { IModalInquiryAttachFileActionPayload } from '../../types/createInquiryActionTypes';
import { ETopicType, IInquiryAttachProps } from '../../types/createInquiryTypes';
import {
    ErrorMessage,
    FormItemContainerStyled,
    InquiryAttachButtonStyled,
    InquiryAttachCloseIconStyled,
    InquiryAttachIconStyled,
    InquiryAttachItemStyled,
    InquiryAttachNameStyled,
    InquiryAttachTypeStyled,
} from './styled/CreateInquiryFormStyled';

export interface ICreateInquiryFormAttach {
    attachFile: (p: IModalInquiryAttachFileActionPayload) => void;
    attachList: IInquiryAttachProps[];
    dettachFile: (p: number) => void;
    translation: ICreateInquiryTranslation;
    type: ETopicType;
}

export const CreateInquiryFormAttach = (props: ICreateInquiryFormAttach) => {
    const [inputValue, setInputValue] = useState('');
    const [fileSizeError, setFileSizeError] = useState<string>('');

    const onFileAttachHandler = (e: ChangeEvent<HTMLInputElement>) => {
        const files: FileList = get(e, 'target.files', []);
        const { filesExceededError, validFiles } = uploadFilesValidation(files, props.translation);

        if (!!filesExceededError) {
            setFileSizeError(filesExceededError);
        }

        validFiles.forEach((file) => {
            props.attachFile({ file, type: props.type });
        });

        setInputValue('');
    };

    const onFileDettachHanlder = (id: number) => {
        props.dettachFile(id);
    };

    const getFilteType = (name: string): string => {
        const match = name.match(/\.[a-zA-Z]+$/);
        return match ? match[0].slice(1) : '';
    };

    const onAttachButtonHandler = () => {
        setFileSizeError('');
        GTMOperations.navigation.inquiryAddFileClickOperation();
    };

    return (
        <FormItemContainerStyled>
            <InquiryAttachButtonStyled onClick={onAttachButtonHandler}>
                <input
                    id="inquiry-attach-file"
                    multiple={true}
                    name="file"
                    type="file"
                    value={inputValue}
                    onChange={onFileAttachHandler}
                />
                <LinkBlock>{props.translation.attachFile}</LinkBlock>
            </InquiryAttachButtonStyled>
            {!!fileSizeError && <ErrorMessage>{fileSizeError}</ErrorMessage>}
            {props.attachList.map((attach: IInquiryAttachProps) => {
                return (
                    <InquiryAttachItemStyled key={attach.fileId}>
                        <InquiryAttachIconStyled>
                            <InquiryAttachTypeStyled>{getFilteType(attach.fileName)}</InquiryAttachTypeStyled>
                        </InquiryAttachIconStyled>
                        <InquiryAttachNameStyled>{attach.fileName}</InquiryAttachNameStyled>
                        <InquiryAttachCloseIconStyled onClick={() => onFileDettachHanlder(attach.fileId)} />
                    </InquiryAttachItemStyled>
                );
            })}
        </FormItemContainerStyled>
    );
};
